import { PolicyReviewHistoryStatus, PolicyReviewStatus, PolicyReviewTask, PolicyStatus } from '@/enum/global.enum'
import { getGlobalStatusColor, getGlobalStatusHexColor } from '@/utils/global-status-color.utils'

/**
 * Converts policy status identifiers to human-readable display labels
 *
 * @param status - The policy status identifier to convert. Can be a PolicyStatus enum value, string, null, or undefined
 * @returns A human-readable string representing the policy status. Returns 'Unknown Status' for unknown/null statuses
 */
const policyStatusLabel = (status?: PolicyStatus | null | string) => {
  if (!status) return 'Unknown Status'

  switch (status) {
    case PolicyStatus.DRAFT:
      return 'Draft'
    case PolicyStatus.REVIEW_REQUESTED:
      return 'Review Requested'
    case PolicyStatus.REVIEW:
      return 'Reviewed'
    case PolicyStatus.APPROVED:
      return 'Approved'
    case PolicyStatus.REJECTED:
      return 'Rejected'
    case PolicyStatus.ARCHIVED:
      return 'Archived'
    default:
      return 'Unknown Status'
  }
}

const policyStatusColor = (status?: PolicyStatus | null | string) => {
  return getGlobalStatusColor(status)
}

const policyStatusBgColorOptions: Partial<Record<PolicyStatus, string>> = {
  [PolicyStatus.DRAFT]: getGlobalStatusHexColor(PolicyStatus.DRAFT),
  [PolicyStatus.REVIEW_REQUESTED]: getGlobalStatusHexColor(PolicyStatus.REVIEW_REQUESTED),
  [PolicyStatus.REVIEW]: getGlobalStatusHexColor(PolicyStatus.REVIEW),
  [PolicyStatus.APPROVED]: getGlobalStatusHexColor(PolicyStatus.APPROVED),
  [PolicyStatus.REJECTED]: getGlobalStatusHexColor(PolicyStatus.REJECTED),
  [PolicyStatus.ARCHIVED]: getGlobalStatusHexColor(PolicyStatus.ARCHIVED),
}

const policyReviewHistoryStatusLabel = (status?: PolicyReviewHistoryStatus | null | string) => {
  if (!status) return 'Unknown'

  switch (status) {
    case PolicyReviewHistoryStatus.APPROVED:
      return 'Approved'
    case PolicyReviewHistoryStatus.REJECTED:
      return 'Rejected'
    case PolicyReviewHistoryStatus.CANCELLED:
      return 'Cancelled'
    default:
      return 'Unknown'
  }
}

const policyReviewHistoryStatusColor = (status?: PolicyReviewHistoryStatus | null | string) => {
  return getGlobalStatusColor(status)
}

const policyReviewStatusLabel = (status: PolicyReviewStatus) => {
  switch (status) {
    case PolicyReviewStatus.PENDING:
      return 'Pending Review'
    case PolicyReviewStatus.APPROVED:
      return 'Approved'
    case PolicyReviewStatus.REJECTED:
      return 'Rejected'
    case PolicyReviewStatus.CANCELLED:
      return 'Cancelled'
    default:
      return status
  }
}

const policyReviewTaskLabel = (task: PolicyReviewTask) => {
  switch (task) {
    case PolicyReviewTask.REVIEWER:
      return 'Reviewer'
    case PolicyReviewTask.APPROVER:
      return 'Approver'
    default:
      return task
  }
}

export {
  policyReviewHistoryStatusColor,
  policyReviewHistoryStatusLabel,
  policyReviewStatusLabel,
  policyReviewTaskLabel,
  policyStatusBgColorOptions,
  policyStatusColor,
  policyStatusLabel,
}
